import React, { useEffect, useRef, useState } from 'react';
import {
  EditorView,
  keymap,
  lineNumbers,
  highlightActiveLine,
  highlightActiveLineGutter,
  drawSelection,
  placeholder as placeholderExtension,
  tooltips,
} from '@codemirror/view';
import { Compartment, EditorState } from '@codemirror/state';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { autocompletion, closeBrackets, closeBracketsKeymap, completionKeymap } from '@codemirror/autocomplete';
import { lintGutter, setDiagnostics } from '@codemirror/lint';
import { bracketMatching, indentOnInput } from '@codemirror/language';
import { DiagnosticOutDTO, RuleMetadataOutDTO } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { diagnosticHint, diagnosticMessage } from '../../i18nKeys';
import { createRuleCompletion, createRuleLanguage, RefOptionsLoader, ruleHighlighting, toEditorDiagnostics } from './ruleLanguage';
import { useTextRefOptionsLoader } from '../builder/entityRefOptions';

const MIN_HEIGHT_PX = 160;
const DEFAULT_HEIGHT_PX = 340;
const HEIGHT_STORAGE_KEY = 'aiDispatcher:editorHeight';

/**
 * The height the user last dragged the editor to.
 *
 * Worth remembering because switching to the builder unmounts this component: without it, every
 * trip through the builder would snap a carefully enlarged editor back to its default.
 */
const readStoredHeight = (): number => {
  try {
    const stored = Number(window.localStorage.getItem(HEIGHT_STORAGE_KEY));
    return Number.isFinite(stored) && stored >= MIN_HEIGHT_PX ? stored : DEFAULT_HEIGHT_PX;
  } catch {
    // Private mode, or site data blocked — the default height is perfectly usable.
    return DEFAULT_HEIGHT_PX;
  }
};

interface ComponentProps {
  value: string;
  onChange: (value: string) => void;
  diagnostics: DiagnosticOutDTO[];
  metadata?: RuleMetadataOutDTO;
  readOnly?: boolean;
  placeholder?: string;
  /** Undefined means the logged-in user's own company — the lists offered are that company's. */
  companyId?: string;
}

/**
 * The editor fills its host, and the host is what the user resizes — so the scrollbar belongs to
 * CodeMirror's own scroller rather than to the box around it.
 */
const editorTheme = EditorView.theme({
  '&': { fontSize: '13px', backgroundColor: '#FFFFFF', height: '100%' },
  '.cm-scroller': { overflow: 'auto' },
  '&.cm-focused': { outline: 'none' },
  '.cm-content': { fontFamily: "'JetBrains Mono', 'SFMono-Regular', Menlo, Consolas, monospace", padding: '8px 0' },
  '.cm-gutters': { backgroundColor: '#FAFAFA', border: 'none', color: '#8C8C8C' },
  '.cm-activeLine': { backgroundColor: 'rgba(254, 80, 0, 0.04)' },
  '.cm-activeLineGutter': { backgroundColor: 'transparent' },
});

/**
 * The DSL editor.
 *
 * CodeMirror owns the document while the user types; this component only pushes a new value in
 * when the source changed elsewhere — the Format action, or a regeneration from the builder.
 * Comparing against the live document before dispatching is what stops that from fighting the
 * user's cursor.
 */
const RuleTextEditor: React.FC<ComponentProps> = ({ value, onChange, diagnostics, metadata, readOnly, placeholder, companyId }) => {
  const { tr } = useTranslationLgs();
  const hostRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // `tr` is rebuilt on every render, so the completion source takes this stable wrapper instead —
  // otherwise the effect below would reconfigure CodeMirror on every keystroke.
  const trRef = useRef(tr);
  trRef.current = tr;
  const translate = useRef((key: string, defaultText?: string, data?: unknown) => trRef.current(key, defaultText, data)).current;
  // Rebuilt every render for the same reason, and wrapped the same way.
  const refOptionsLoader = useTextRefOptionsLoader(companyId);
  const refOptionsLoaderRef = useRef(refOptionsLoader);
  refOptionsLoaderRef.current = refOptionsLoader;
  const loadRefOptions = useRef<RefOptionsLoader>(entityRefType => refOptionsLoaderRef.current(entityRefType)).current;

  const [height] = useState(readStoredHeight);

  // The user resizes the host directly (CSS `resize`), so the height is observed rather than
  // controlled — React never fights the drag.
  useEffect(() => {
    const host = hostRef.current;
    if (!host || typeof ResizeObserver === 'undefined') return undefined;

    let handle: ReturnType<typeof setTimeout>;
    const observer = new ResizeObserver(entries => {
      const observed = Math.round(entries[0]?.contentRect.height ?? 0);
      if (observed < MIN_HEIGHT_PX) return;
      clearTimeout(handle);
      handle = setTimeout(() => {
        try {
          window.localStorage.setItem(HEIGHT_STORAGE_KEY, String(observed));
        } catch {
          // Nothing to do — the height simply will not be remembered.
        }
      }, 300);
    });
    observer.observe(host);

    return () => {
      clearTimeout(handle);
      observer.disconnect();
    };
  }, []);

  const languageCompartment = useRef(new Compartment());
  const completionCompartment = useRef(new Compartment());
  const readOnlyCompartment = useRef(new Compartment());

  useEffect(() => {
    if (!hostRef.current) return undefined;

    const view = new EditorView({
      parent: hostRef.current,
      state: EditorState.create({
        doc: value,
        extensions: [
          lineNumbers(),
          highlightActiveLine(),
          highlightActiveLineGutter(),
          drawSelection(),
          history(),
          indentOnInput(),
          bracketMatching(),
          closeBrackets(),
          lintGutter(),
          // Diagnostics render into the body, not into the editor box. The box has to clip its
          // own content to be resizable, which would otherwise cut off a hint on the last line —
          // exactly the line most likely to still be wrong. In the body it also has room to flip
          // above the cursor when there is none below.
          tooltips({ parent: document.body }),
          keymap.of([...closeBracketsKeymap, ...defaultKeymap, ...historyKeymap, ...completionKeymap]),
          languageCompartment.current.of([]),
          completionCompartment.current.of(autocompletion()),
          readOnlyCompartment.current.of([]),
          ruleHighlighting,
          editorTheme,
          EditorView.lineWrapping,
          placeholder ? placeholderExtension(placeholder) : [],
          EditorView.updateListener.of(update => {
            if (update.docChanged) onChangeRef.current(update.state.doc.toString());
          }),
        ],
      }),
    });
    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // Built once; everything variable is reconfigured through compartments below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The language and the completion source both depend on metadata, which arrives after mount.
  useEffect(() => {
    const view = viewRef.current;
    if (!view || !metadata) return;
    view.dispatch({
      effects: [
        languageCompartment.current.reconfigure(createRuleLanguage(metadata.reservedWords ?? [])),
        completionCompartment.current.reconfigure(autocompletion({ override: [createRuleCompletion(metadata, translate, loadRefOptions)] })),
      ],
    });
  }, [metadata, translate, loadRefOptions]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    view.dispatch({ effects: readOnlyCompartment.current.reconfigure(EditorState.readOnly.of(Boolean(readOnly))) });
  }, [readOnly]);

  // Only push a value the editor does not already hold, or typing would fight itself.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const current = view.state.doc.toString();
    if (current === value) return;
    view.dispatch({ changes: { from: 0, to: current.length, insert: value } });
  }, [value]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    view.dispatch(
      setDiagnostics(
        view.state,
        toEditorDiagnostics(diagnostics, view.state.doc, diagnostic => {
          const hint = diagnosticHint(tr, diagnostic);
          const message = diagnosticMessage(tr, diagnostic);
          return hint ? `${message}\n${hint}` : message;
        }),
      ),
    );
  }, [diagnostics, tr]);

  return <div ref={hostRef} className="resize-y overflow-hidden rounded-md border border-solid border-base-tertiary" style={{ height, minHeight: MIN_HEIGHT_PX }} />;
};

export default RuleTextEditor;
